import type {
  ExternalMcpAuth,
  ExternalMcpTransport,
  ExternalMcpTrust,
} from "@ctrl/controller-rpc";

/**
 * How the user authenticates to a vetted *remote* (HTTP) MCP package.
 *
 * Stdio packages usually take credentials via {@link McpPackageEnvVar} instead.
 */
export type McpPackageAuthKind = "none" | "bearer" | "header";

export type McpPackageAuth =
  | { kind: "none" }
  | {
      kind: "bearer";
      /** Shown in the install form as the token field placeholder. */
      placeholder?: string;
    }
  | {
      kind: "header";
      name: string;
      placeholder?: string;
    };

/** An environment variable the stdio MCP server expects at launch. */
export type McpPackageEnvVar = {
  name: string;
  /** Defaults to true when omitted. */
  required?: boolean;
  /** Treat as a secret in the install form (default true). */
  secret?: boolean;
  placeholder?: string;
  description?: string;
  /**
   * When set, the install UI should also pass the value as CLI args
   * `[cliFlag, value]` (some official servers take `--api-key` instead of env).
   */
  cliFlag?: string;
};

/**
 * A curated, Routine-vetted MCP server the user can install in one click
 * (or with documented credentials). Distinct from free-form custom servers in
 * External MCP settings.
 */
export type McpPackage = {
  /** Stable catalog id — not the same as a configured server's nanoid. */
  id: string;
  name: string;
  description: string;
  /** Optional docs / homepage for the package. */
  docsUrl?: string;
  /** Remote logo; falls back to a generic icon in the UI when absent. */
  logoUri?: string;
  /**
   * npm package id used with `npx -y <npmPackage>` (and for install matching).
   * Required for stdio catalog entries.
   */
  npmPackage?: string;
  transport: ExternalMcpTransport;
  auth: McpPackageAuth;
  /** Env vars to collect for stdio servers (values filled at install time). */
  envVars?: readonly McpPackageEnvVar[];
  /**
   * Trust applied on install. Prefer `readOnly` when the server annotates
   * tools; use `all` when tools omit `readOnlyHint` (otherwise they are
   * filtered out of the agent registry).
   */
  defaultTrust: ExternalMcpTrust;
  /** Explains a non-obvious `defaultTrust` choice to the user. */
  trustNote?: string;
};

/** A vetted package plus whether the user already has it configured. */
export type McpPackageItem = McpPackage & {
  /** Configured external MCP server id, when installed on this device. */
  installedServerId: string | null;
};

/** Build the auth value stored on an external MCP server from package auth. */
export const authFromPackage = (
  auth: McpPackageAuth,
  credential?: string
): ExternalMcpAuth => {
  switch (auth.kind) {
    case "none":
      return "none";
    case "bearer":
      return { token: credential ?? "" };
    case "header":
      return { name: auth.name, value: credential ?? "" };
  }
};
